import React, { useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Button, CircularProgress, Typography } from "@mui/material";
import Icon from "@mdi/react";
import { mdiDoctor, mdiAccountHeart, mdiAccountGroup, mdiCheckCircle } from "@mdi/js";
import { UserServices, NotistackServices } from "shared/services";
import { useAccount } from "shared/hooks/useAccount";
import { MedicalProfileType } from "shared/model/AccountModel";
import styles from "../ProfilePage/styles.module.scss";

export const MedicalProfilePage: React.FC = () => {
	const account = useAccount();
	const currentProfile = account?.settings?.medicalProfile;
	const [profile, setProfile] = useState<MedicalProfileType | null>(currentProfile ?? null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	const handleSave = async () => {
		if (!profile) return;
		setSaving(true);
		setSaved(false);
		try {
			await UserServices.updateUser({
				settings: { ...account?.settings, medicalProfile: profile },
			});
			NotistackServices.showSuccessMessage("Perfil médico atualizado com sucesso!");
			setSaved(true);
		} catch {
			NotistackServices.showErrorMessage("Erro ao atualizar perfil médico.");
		} finally {
			setSaving(false);
		}
	};

	const hasChanged = profile !== currentProfile;

	return (
		<div className={styles.profilePage}>
			<Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
				Perfil Médico
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Selecione como você utiliza a plataforma de telemedicina. Isso define quais funcionalidades estarão disponíveis para você.
			</Typography>

			<ToggleButtonGroup
				value={profile}
				exclusive
				onChange={(_, val) => {
					if (val) {
						setProfile(val);
						setSaved(false);
					}
				}}
				sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}
			>
				<ToggleButton
					value="doctor"
					sx={{
						flex: 1,
						minWidth: 160,
						display: "flex",
						flexDirection: "column",
						gap: 1,
						py: 2,
						borderRadius: "12px !important",
						border: "1px solid var(--mui-palette-divider) !important",
					}}
				>
					<Icon path={mdiDoctor} size={1.5} />
					Sou Médico(a)
				</ToggleButton>
				<ToggleButton
					value="patient"
					sx={{
						flex: 1,
						minWidth: 160,
						display: "flex",
						flexDirection: "column",
						gap: 1,
						py: 2,
						borderRadius: "12px !important",
						border: "1px solid var(--mui-palette-divider) !important",
					}}
				>
					<Icon path={mdiAccountHeart} size={1.5} />
					Sou Paciente
				</ToggleButton>
				<ToggleButton
					value="both"
					sx={{
						flex: 1,
						minWidth: 160,
						display: "flex",
						flexDirection: "column",
						gap: 1,
						py: 2,
						borderRadius: "12px !important",
						border: "1px solid var(--mui-palette-divider) !important",
					}}
				>
					<Icon path={mdiAccountGroup} size={1.5} />
					Ambos
				</ToggleButton>
			</ToggleButtonGroup>

			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Button
					variant="contained"
					disabled={!hasChanged || saving || !profile}
					onClick={handleSave}
					startIcon={saving ? <CircularProgress size={16} /> : undefined}
				>
					{saving ? "Salvando..." : "Salvar"}
				</Button>
				{saved && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
						<Icon path={mdiCheckCircle} size={0.8} />
						<Typography variant="body2">Salvo</Typography>
					</Box>
				)}
			</Box>
		</div>
	);
};
